import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { expertUrl, productUrl, companyUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import GallerySection from "../../profiles/GallerySection";
import AboutSection from "../../profiles/sections/AboutSection";
import EmploymentSection, { toEmploymentRow } from "../../profiles/sections/EmploymentSection";
import RepresentsSection from "../../profiles/RepresentsSection";
import useProductCardDetails from "../../profiles/data/use_product_card_details";
import Tag from "../../profiles/Tag";
import HashtagsCollapsible from "../../hashtags/HashtagsCollapsible";
import Quotes from "../../quotes/Quotes";
import MediaItems from "../../media_items/MediaItems";
import { ExpertContext } from "../data/expert_context_management";
import useExpertRepresentationsApi from "../data/use_expert_representations_api";
import useStoryIdeas from "../data/use_story_ideas";
import StoryIdeasCard from "./story_ideas_card";

// The design draws a handful of chips on a person row with no overflow control, so the cap lives
// with the caller rather than in the row. The provider ranks each entity's tags most-tagged first,
// so this takes the top few rather than an arbitrary few.
const ROW_TAG_LIMIT = 3;

// DOM id of the hybrid's "Represents" section, shared with the Source Directory PR cards: a
// hybrid's "Represents" link deep-links to `/experts/:slug#<this>` (the PR profile URL redirects
// hybrids here) so the expert page lands scrolled to it.
export const EXPERT_ALSO_REPRESENTS_SECTION_ID = "expert-section-also-represents";

function hashtagIndexUrl(hashtagName) {
  return `/experts?hashtag=${encodeURIComponent(hashtagName)}`;
}

function buildHashtagUrl(hashtag) {
  return hashtagIndexUrl(hashtag.hashtagName);
}

function rowTags(tags) {
  if (!tags || tags.length === 0) return null;

  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {tags.slice(0, ROW_TAG_LIMIT).map((tag) => (
        <Tag key={tag.hashtag_id} href={hashtagIndexUrl(tag.hashtag_name)}>
          {tag.hashtag_name}
        </Tag>
      ))}
    </div>
  );
}

function companyHref(slug, migrateId) {
  return slug ? companyUrl({ slug, migrate_id: migrateId }) : undefined;
}

// Expert content as a stack of shared <SectionCard>s. About and Employment are core sections: they
// always render, with neutral empty states, so the page frame is visible even on a sparse profile — and
// an editable viewer keeps the working pen either way. Expertise no longer has a card of its own; its
// chips moved inside About's eyebrow slot. Quotes and Media self-fetch and report back whether they came
// up empty, so a non-editable viewer never sees an empty shell (an editable one always does, with the
// muted empty copy, since they're the one who could fill it in). "Represents" is the one relational
// shell left in the main column (Represented by has moved to the rail — see Expert.jsx): it renders only
// when the data exists, since most self-representing experts legitimately represent nothing else, and an
// always-on empty card would be noise rather than frame. The one exception is a viewer who can manage the
// represented entities, who needs the section as the surface to do it from.
// Quotes/MediaItems reuse the existing self-fetch components; migrating them onto the provider comes with
// the fuller Expert buildout.
function ExpertSections({ onQuotesEmptyChange = () => {} }) {
  const context = useContext(ExpertContext);
  const {
    expert,
    hashtags,
    companyJobs,
    alsoRepresents,
    representedExpertTags,
    hybrid,
    selfProfileId,
    viewerContext,
    setOpenEditModal,
    generalError,
    setGeneralError,
    activeTabRequest,
    setActiveTabRequest,
    addQuoteRequest,
  } = context;
  const { removeRepresentation } = useExpertRepresentationsApi(context);
  const storyIdeas = useStoryIdeas({ expertId: expert.id });

  // "Not yet settled" starts as null so a non-editable viewer can tell "still loading" (hidden) apart
  // from "confirmed empty" (also hidden, but permanently) — both render behind the same d-none wrapper,
  // never a populated shell that later disappears.
  const [quotesEmpty, setQuotesEmpty] = useState(null);
  const handleQuotesEmptyChange = useCallback(
    (empty) => {
      setQuotesEmpty(empty);
      onQuotesEmptyChange(empty);
    },
    [onQuotesEmptyChange],
  );
  const [mediaEmpty, setMediaEmpty] = useState(null);

  // The "Add a quote" checklist row opens the editor down in the Quotes section, so scroll it into
  // view on the same signal. Skip the mount pass so the page doesn't jump to Quotes on load.
  const initialAddQuoteRequest = useRef(addQuoteRequest);
  useEffect(() => {
    if (addQuoteRequest === initialAddQuoteRequest.current) return;
    document.getElementById("expert-section-quotes")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [addQuoteRequest]);

  // Removing the last represented entity would otherwise collapse this section mid-write — taking the
  // removal's confirmation, and the error a failed destroy has to report, with it. removeRepresentation
  // drops the entity from the collection in the same synchronous block this flag is raised in, so the
  // render that sees the emptied collection also sees the removal in flight; the section collapses on the
  // next render, once the reload has landed. Resolves exactly what the hook resolves — the confirmation
  // closes on that boolean. A section-local flag, not the provider's page-wide isUpdating, so an
  // unrelated save can't reveal an empty section.
  const [removalInFlight, setRemovalInFlight] = useState(false);
  const removeEntity = async (row) => {
    setRemovalInFlight(true);
    try {
      const removed = await removeRepresentation(row);
      return removed;
    } finally {
      setRemovalInFlight(false);
    }
  };

  // Employment rows hang off the expert ENTITY, so the affordance gates on entity authority
  // (`editable`) rather than `profileEditable`. It arrives null rather than false for an anonymous
  // viewer, hence the coercion — never a comparison against false.
  const employmentEditable = !!viewerContext?.editable;

  // A job flagged `personHidden` is hidden from this expert's own profile, so it must not render here.
  // Filter the DISPLAY collection only: RLS still returns hidden rows to the expert's representatives,
  // and the provider keeps them for the management surface.
  const jobs = (companyJobs || []).filter((job) => !job.personHidden);
  const entities = alsoRepresents || [];

  const representedExperts = entities.filter((e) => e.promotedEntityType === "Id::PromoEntity::Expert");
  const representedCompanies = entities.filter((e) => e.promotedEntityType === "Id::PromoEntity::Company");
  const representedProducts = entities.filter((e) => e.promotedEntityType === "Id::PromoEntity::Product");
  const { details: productDetails, onShow: showProductCards } = useProductCardDetails(
    representedProducts.map((entity) => entity.promotedEntityId),
  );

  // These representations belong to the promoter profile behind a self-representing expert, so editing
  // them is authority over THAT profile (`profileEditable`, seeded from Id::ProfilePolicy#update?) —
  // never `editable`, which a co-representing PR passes without owning the promoter side. The profile id
  // is part of the gate because a create needs it. profileEditable is a strict boolean from the server.
  const representsEditable = viewerContext?.profileEditable === true && !!selfProfileId;

  // Adding is offered only where the create can actually succeed: Id::RepresentationPolicy#create?
  // admits admins, PRs and hybrids, and a promoter who self-represents but represents no other expert
  // is type_promoter_expert — it would be refused. This gates all three Add ▾ items (Expert, Product,
  // Company): the policy check does not distinguish by promoted-entity type, so the same boolean that
  // used to gate only the Experts tab's own button is the correct gate for all three now that adding is
  // page-level rather than tab-scoped.
  const canAddRepresentation = representsEditable && hybrid;

  const representsTabs = [
    {
      key: "experts",
      label: "Experts",
      // Rows are keyed by the promoted ENTITY: the view fans out per job, so an entity has no single
      // representation id. removeRepresentation resolves the row back to every id it must destroy.
      rows: representedExperts.map((entity) => ({
        id: entity.promotedEntityId,
        href: expertUrl({ slug: entity.promotedEntitySlug, migrate_id: entity.promotedEntityMigrateId }),
        imageUrl: entity.promotedEntityProfileImageUrl,
        title: entity.promotedEntityName,
        subtitleTitle: entity.promotedExpertJobTitle,
        subtitleCompanyName: entity.promotedCompanyName,
        subtitleCompanyHref: companyHref(entity.promotedCompanySlug, entity.promotedCompanyMigrateId),
        tags: rowTags((representedExpertTags || {})[entity.promotedEntityId]),
      })),
      onRemove: removeEntity,
      emptyText: "No experts represented yet.",
    },
    {
      key: "products",
      label: "Products",
      layout: "cards",
      perPage: 4,
      onShow: showProductCards,
      rows: representedProducts.map((entity) => ({
        id: entity.promotedEntityId,
        href: productUrl({ slug: entity.promotedEntitySlug, migrate_id: entity.promotedEntityMigrateId }),
        imageUrl: entity.promotedEntityProfileImageUrl,
        title: entity.promotedEntityName,
        // A product's subtitle is its maker, which is a page of its own — no job title involved.
        subtitleCompanyName: entity.promotedCompanyName,
        subtitleCompanyHref: companyHref(entity.promotedCompanySlug, entity.promotedCompanyMigrateId),
        price: productDetails[entity.promotedEntityId]?.price,
        description: productDetails[entity.promotedEntityId]?.description,
      })),
      onRemove: removeEntity,
      emptyText: "No products represented yet.",
    },
    {
      key: "companies",
      label: "Companies",
      rows: representedCompanies.map((entity) => ({
        id: entity.promotedEntityId,
        href: companyUrl({ slug: entity.promotedEntitySlug, migrate_id: entity.promotedEntityMigrateId }),
        imageUrl: entity.promotedEntityProfileImageUrl,
        title: entity.promotedEntityName,
      })),
      onRemove: removeEntity,
      emptyText: "No companies represented yet.",
    },
  ];

  const aboutBlock = (
    <AboutSection
      id="expert-section-about"
      eyebrowLabel="Expert on:"
      eyebrowContent={
        hashtags.length > 0 ? <HashtagsCollapsible hashtags={hashtags} hashtagUrlResolver={buildHashtagUrl} /> : null
      }
      text={expert.description}
      editable={employmentEditable}
      onEdit={() => setOpenEditModal("HEADER")}
      editButtonId="modal-button-ABOUT"
    />
  );

  const employmentBlock = (
    <EmploymentSection
      id="expert-section-employment"
      rows={jobs.map(toEmploymentRow)}
      showDates
      editable={employmentEditable}
      onEdit={() => setOpenEditModal("EMPLOYMENT")}
      editButtonId="modal-button-EMPLOYMENT"
    />
  );

  // removalInFlight holds the section open across a removal that optimistically emptied the
  // collection — see removeEntity above.
  const alsoRepresentsBlock = (entities.length > 0 || canAddRepresentation || removalInFlight) && (
    <RepresentsSection
      id={EXPERT_ALSO_REPRESENTS_SECTION_ID}
      title="Represents"
      tabs={representsTabs}
      editable={representsEditable}
      addOptions={
        canAddRepresentation
          ? [
              { label: "Expert", icon: "fa-solid fa-user", onAdd: () => setOpenEditModal("EXPERTS") },
              { label: "Product", icon: "fa-solid fa-box-open", onAdd: () => setOpenEditModal("PRODUCTS") },
              { label: "Company", icon: "fa-solid fa-building", onAdd: () => setOpenEditModal("COMPANIES") },
            ]
          : undefined
      }
      activeTabRequest={activeTabRequest}
      onActiveTabRequestHandled={() => setActiveTabRequest(null)}
      // generalError is shared with the edit modals, so one raised inside an open modal also lands
      // here — harmlessly, behind the backdrop.
      generalError={generalError}
      clearGeneralError={() => setGeneralError(null)}
    />
  );

  const quotesBlock = (
    <div className={employmentEditable || quotesEmpty === false ? "" : "d-none"}>
      <SectionCard id="expert-section-quotes" title="Recent Quotes">
        <Quotes
          // The persisted owner_type is the concrete STI class (Rails' polymorphic_name), never the
          // abstract "Id::PromoEntity" -- a read against the base name matches zero rows.
          ownerType="Id::PromoEntity::Expert"
          ownerId={expert.id}
          ownerResource="experts"
          editable={employmentEditable}
          showTitle={false}
          emptyMessage="No quotes added yet."
          onEmptyChange={handleQuotesEmptyChange}
          openAddSignal={addQuoteRequest}
        />
      </SectionCard>
    </div>
  );

  const mediaBlock = (
    <div className={employmentEditable || mediaEmpty === false ? "" : "d-none"}>
      <SectionCard id="expert-section-media" title="Featured In">
        <MediaItems
          // Same concrete-STI-owner_type fix as Quotes above.
          ownerType="Id::PromoEntity::Expert"
          ownerId={expert.id}
          ownerResource="experts"
          editable={employmentEditable}
          showTitle={false}
          emptyMessage="No media added yet."
          onEmptyChange={setMediaEmpty}
        />
      </SectionCard>
    </div>
  );

  // Story Ideas is content, not an owner-only widget (see StoryIdeasCard), so it needs no
  // employmentEditable branch the way quotesBlock/mediaBlock do above — it self-collapses on empty
  // for every viewer via the hook's own settle-empty contract.
  const storyIdeasBlock = (
    <StoryIdeasCard pitches={storyIdeas.pitches} isLoading={storyIdeas.isLoading} editable={employmentEditable} />
  );

  // The shared supporting-images gallery, display-only this round (see GallerySection). Sits after
  // Featured In: it is the same "media about this expert" family as the section above it, and it
  // self-collapses for an expert with no supporting images, so no editable-viewer branch is needed
  // the way quotesBlock/mediaBlock carry.
  const galleryBlock = (
    <GallerySection id="expert-section-gallery" title="Headshots" images={expert.supporting_images} />
  );

  return (
    <div className="container-fluid px-0">
      {aboutBlock}
      {/* The hybrid canvas draws Represents directly after About, before Employment; the plain expert
          canvas keeps Employment first. Two engines share this file, so the order branches on which
          canvas is being drawn rather than being "fixed" to one order later. Represented-by no longer
          renders here at all — it moved to the rail (see Expert.jsx), so this branch is otherwise
          unchanged. */}
      {hybrid ? (
        <>
          {alsoRepresentsBlock}
          {employmentBlock}
        </>
      ) : (
        <>
          {employmentBlock}
          {alsoRepresentsBlock}
        </>
      )}
      {quotesBlock}
      {mediaBlock}
      {galleryBlock}
      {storyIdeasBlock}
    </div>
  );
}

ExpertSections.propTypes = {
  onQuotesEmptyChange: PropTypes.func,
};

export default ExpertSections;
